import { useEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { matchRobotToDesiredSpec, type SpecMatchResult, type SpecMatchTier } from '@bhops/shared';
import {
  assignmentsApi,
  type AssignSelection,
  type AssignedRobotRef,
  type AssignmentCandidates,
  type ContractAssignStage,
  type PendingContract,
} from '@/api/assignments';
import { modelsApi } from '@/api/models';
import { scheduleDatesApi } from '@/api/scheduleDates';
import { EventBoard, EventStripCard, assignableEvents } from './EventAssignSection';
import {
  AssignPickList,
  AssignStage,
  PickPanel,
  StripCard,
  chipCenters,
  chipFrom,
  compareRobotNumber,
  dayInfo,
  isSoon,
  lineHeading,
  type DragLoad,
  type FlyRequest,
  type LineHint,
  type PickRow,
  type PickSection,
  type SlotTone,
  type StageHandle,
  type StageLine,
  type StageSlot,
} from './assignment-stage';
import { Icon, type IconName } from '@/components/Icon';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { NewBadge } from '@/components/NewBadge';
import { RobotEntityTag } from '@/pages/robots/RobotEntityTag';
import { Tooltip } from '@/components/Tooltip';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { canEditMfg } from '@/permissions';
import { robotStatusLabel } from '@/lib/enum-labels';
import { kstYmd, kstYmdHyphen } from '@/lib/format-date';
import { CUSTOMER_MASTER_STATUS_LABEL, type CustomerMasterStatus } from '@/api/customer-master';

// docs/plan/team-dashboards-and-production-handoff/plan.md §8.3 +
// 개선계획 §2 (2026-07-26) — 좌우 개편.
//
// 2026-11-04 오너 — 시안 5 '장착대'로 바꿨다("여기도 ui 바꿀거야. 기능은 유지할거고. 로봇 상세에서 부품
// 장착하는거마냥 드래그앤드랍으로 배정하는 방식도 나쁘지 않은것 같고" → "5번으로 하고, 그냥 클릭만 해도
// 여러개 잡을 수 있게 하자. 드래그로 여러개 잡을 수도 있게 하고").
//   위 = 배정할 고객사 · 이벤트 카드 띠(설치일이 큰 숫자), 아래 왼쪽 = 창고 로봇(누르면 고르고, 빈 곳에서
//   끌면 여러 대), 아래 가운데 = 무대(계약 줄마다 필요한 대수만큼 로봇 자리 — 끌어다 놓고 저장).
// 고객사를 고르기 전에는 창고 로봇을 띄우지 않는다 — 전 재고를 계약 없이 보여주면 어느 계약 기준으로
// 완전일치를 판정할지가 없기 때문이다. 그리기 · 끌기 조각은 assignment-stage.tsx.

// S1 Phase 11C (R2 #7) — customerStatus 라벨(한글). 백엔드가 보내는 문자열을
// 알려진 status union 일 때만 매핑하고, 아니면 원문 그대로 표시(방어적).
function customerStatusLabel(status: string): string {
  return CUSTOMER_MASTER_STATUS_LABEL[status as CustomerMasterStatus] ?? status;
}

// S2 Phase 2.9 (§9.4) — 제조 계획 후보의 spec 한 줄 표현 ("GEN2 · White · Right").
// 모델 라벨 + 채워진 축만 · 로 join. 모든 축 미지정이면 모델 라벨만.
function formatPlanSpec(
  plan: { color: string | null; sensorType: string | null; variantValue: string | null },
  modelLabel: string,
): string {
  const parts = [
    plan.color?.trim(),
    plan.variantValue?.trim(),
    plan.sensorType?.trim() ? `불감지 ${plan.sensorType.trim()}` : null,
  ].filter((p): p is string => !!p);
  return parts.length > 0 ? `${modelLabel} · ${parts.join(' · ')}` : modelLabel;
}

// 배정된 로봇의 옵션 한 줄(26-07-30 오너 요구) — 카드를 펼쳤을 때 "416 / 414" 번호만
// 보여선 어느 것을 교체할지 고를 수 없다. **왼쪽 재고 목록과 같은 표기**(formatPlanSpec)를
// 그대로 재사용한다 — 같은 화면에서 두 벌의 표기가 돌아다니면 그게 더 헷갈린다.
// 옵션을 하나도 모르면(구버전 서버) null → 화면은 번호만 보여준다.
function formatRobotSpec(
  r: { color?: string | null; variantValue?: string | null; sensorType?: string | null },
  modelLabel: string,
): string | null {
  if (!r.color && !r.variantValue && !r.sensorType) return null;
  return formatPlanSpec(
    {
      color: r.color ?? null,
      variantValue: r.variantValue ?? null,
      sensorType: r.sensorType ?? null,
    },
    modelLabel,
  );
}

const normSpec = (v: string | null | undefined) => v?.trim().toLowerCase() ?? '';
// 공백만 있는 값은 '없음'으로 본다 — DB 에 빈 문자열이 들어간 줄을 값 있는 것으로 세면
// "요청 ↔ 로봇" 대조가 빈칸끼리 일치했다고 말하게 된다.
const trimOrNull = (v: string | null | undefined): string | null => {
  const t = v?.trim();
  return t ? t : null;
};

type ContractLine = NonNullable<PendingContract['lines']>[number];

/**
 * 추가수정(26-08-14) §2 — 배정된 로봇이 **어느 계약 줄의 자리**를 채우고 있는가.
 *
 * 🔴 종전에는 로봇을 `lines.find(l => l.modelId === r.modelId)` 로 그 모델의 **첫 줄**
 *   하나와만 대조했다. 같은 모델을 사양만 다르게 두 줄로 계약한 고객사(오너 제보 —
 *   설장어/장어애락)에서는 **둘째 줄에 맞춰 옳게 배정한 로봇**이 첫 줄과 비교돼
 *   '요청과 다름'으로 찍혔다. 배정은 옳고 화면 계산만 틀린 것이다.
 *
 * 이 결함은 같은 화면 **왼쪽 후보 목록**이 이미 한 번 겪고 닫았다(서버
 * assignments.service.ts round5 #2c: "(구) desiredByModel 은 같은 모델 여러 라인을
 * '첫 라인 하나'로 대표해 … Right 로봇이 '불일치'로 표시되어 Left 2대 선택을 유도했다").
 * 이 배지는 그 수정 **이후**(26-07-30 옵션 3종)에 새로 만든 코드라 같은 실수가 다시
 * 들어왔다. 그래서 판정 규칙을 새로 짓지 않고 **서버의 라인 버킷 귀속을 그대로 이식**한다
 * — 왼쪽에서 '완전일치'였던 로봇이 배정하고 나면 오른쪽에서 '요청과 다름'이 되는,
 * 한 화면이 같은 로봇을 두고 앞뒤로 다른 말을 하는 상태를 닫는 것이 목적이다.
 *
 * 규칙(서버와 동일): 로봇마다 같은 모델의 **자리가 남은** 줄들을 사양 일치도로 정렬해
 * (tier 우선, 동률이면 score 높은 쪽) 첫 줄에 귀속시킨다. 남은 자리가 없으면 그 모델의
 * 첫 줄로 떨어진다(초과 배정 — 서버 폴백과 같다). 모델을 모르는 로봇(구버전 서버 응답)은
 * 짝이 없다 → 대조하지 않는다.
 *
 * 그 뒤에 **맞바꾸기 한 단계**를 더 돌린다(아래 정정 주석). 서버에는 없는 단계지만 줄별
 * 배정 대수를 바꾸지 않으므로 서버가 세는 값과는 어긋나지 않는다 — 바뀌는 것은 "어느 줄과
 * 대조해 무엇이 다르다고 말하는가"뿐이고, 그 판단은 이 화면에만 있다.
 */
const TIER_RANK: Record<SpecMatchTier, number> = {
  exact: 0,
  partial: 1,
  unspecified: 2,
  mismatch: 3,
};

function pairRobotsToLines(
  robots: AssignedRobotRef[],
  lines: PendingContract['lines'],
): Map<string, ContractLine | null> {
  const buckets = (lines ?? []).map((line) => ({ line, filled: 0 }));
  // 로봇 i 가 채우는 버킷 번호(없으면 null). 아래 맞바꾸기 단계가 이 배열만 고친다.
  const at: Array<number | null> = robots.map(() => null);

  robots.forEach((r, i) => {
    if (!r.modelId) return;
    const ranked = buckets
      .map((b, idx) => ({ b, idx }))
      .filter(({ b }) => b.line.modelId === r.modelId && b.filled < b.line.quantity)
      .map(({ b, idx }) => ({
        idx,
        m: matchRobotToDesiredSpec(
          {
            color: b.line.desiredColor ?? null,
            direction: b.line.desiredDirection ?? null,
            sensor: b.line.desiredSensor ?? null,
          },
          { color: r.color, variantValue: r.variantValue, sensorType: r.sensorType },
        ),
      }))
      .sort((x, y) => TIER_RANK[x.m.tier] - TIER_RANK[y.m.tier] || y.m.score - x.m.score);
    const fallback = buckets.findIndex((b) => b.line.modelId === r.modelId);
    const chosen = ranked[0]?.idx ?? (fallback >= 0 ? fallback : null);
    if (chosen !== null) {
      buckets[chosen].filled += 1;
      at[i] = chosen;
    }
  });

  // ── 맞바꾸기 한 단계 더 (추가수정 26-08-14 §2 정정) ─────────────────────────
  //
  // 🔴 위 greedy 는 로봇을 하나씩 보며 그 시점 최선을 잡고 **되돌리지 않는다**(서버와
  //   같은 방식이다). 그래서 먼저 처리된 로봇이 자리를 선점하면, 뒤 로봇이 자기가
  //   정확히 맞는 줄을 못 잡고 남은 줄과 대조돼 **거짓 '요청과 다름'** 이 뜬다.
  //   실제로 두 가지 모양으로 재현됐다:
  //     ① 두 줄이 서로 다른 축만 지정한 계약 — 앞 로봇이 양쪽 다 맞는데 점수 높은 줄을
  //        가져가 버린다(줄 둘을 모두 정확히 채운 배정인데 배지가 뜬다).
  //     ② 사양이 비어 있는 로봇이 먼저 오면 자리를 차지해, 그 줄에 딱 맞는 로봇이 밀린다.
  //   ①은 이 차수 전(첫 줄과만 대조)에는 배지가 없던 입력이다 — 고치지 않으면 거짓 경고를
  //   없애러 와서 **새 거짓 경고를 만들고** 간다.
  //
  // 그래서 자리 배치가 끝난 뒤, 같은 모델 로봇 두 대의 줄을 맞바꿔 **어긋난 축의 총합이
  // 줄어들면** 바꾼다. 줄어들 때만 바꾸므로 반드시 끝난다.
  //
  // ⚠ 서버와 어긋나지 않는다: 맞바꾸기는 같은 모델 로봇 둘이 서로의 줄을 교환할 뿐이라
  //   **줄별 배정 대수(buckets[].filled)가 그대로다.** 서버가 이 귀속으로 세는 것은 그
  //   대수뿐이고(라인별 배정/남음), "어느 줄과 대조해 무엇이 다르다고 말하는가"는 이
  //   화면에만 있는 판단이다. 바꾸는 것은 그 판단뿐이다.
  //
  // 기준은 배지가 세는 것과 **같은 함수**를 쓴다 — 짝을 고르는 잣대와 화면에 그리는
  // 잣대가 다르면 "왜 저기 붙었나"를 설명할 수 없다.
  const differCost = (r: AssignedRobotRef, line: ContractLine): number =>
    specAxisChecks(r, line, null).filter((x) => x.state === 'differ').length;

  for (let pass = 0; pass < robots.length; pass += 1) {
    let improved = false;
    for (let i = 0; i < robots.length; i += 1) {
      for (let j = i + 1; j < robots.length; j += 1) {
        const a = at[i];
        const b = at[j];
        if (a === null || b === null || a === b) continue;
        if (robots[i].modelId !== robots[j].modelId) continue;
        const before =
          differCost(robots[i], buckets[a].line) + differCost(robots[j], buckets[b].line);
        const after =
          differCost(robots[i], buckets[b].line) + differCost(robots[j], buckets[a].line);
        if (after < before) {
          at[i] = b;
          at[j] = a;
          improved = true;
        }
      }
    }
    if (!improved) break;
  }

  return new Map(
    robots.map((r, i) => {
      const idx = at[i];
      return [r.id, idx === null ? null : buckets[idx].line] as const;
    }),
  );
}

/**
 * 축 하나의 대조 결과.
 *
 * '다름'과 '로봇 값 없음'을 나누는 이유는 **고칠 곳이 다르기 때문**이다. 다름은 계약과
 * 로봇 중 어느 쪽이 틀렸는지 사람이 정해야 하고, 없음은 로봇 상세에서 채우면 끝난다.
 * 종전에는 둘 다 '요청과 다름'(주황) 한 덩어리라, 사양을 아직 안 적은 로봇이 계약과
 * 어긋난 로봇처럼 보였다.
 */
type SpecAxisState = 'match' | 'differ' | 'unknown';
interface SpecAxisCheck {
  label: string;
  state: SpecAxisState;
  desired: string;
  actual: string | null;
}

// 희망값이 없는 축(미정)은 대조하지 않는다 — 미정을 불일치로 세면 거의 모든 줄이 붉어져
// 신호가 죽는다(종전 규칙 그대로).
function specAxisChecks(
  r: { color?: string | null; variantValue?: string | null; sensorType?: string | null },
  line: ContractLine | null,
  variantAxisLabel: string | null | undefined,
): SpecAxisCheck[] {
  if (!line) return [];
  const axes: Array<[string, string | null | undefined, string | null | undefined]> = [
    ['색상', line.desiredColor, r.color],
    // 구분 사양 축의 **이름**은 모델마다 다르다(GEN2·헤드리스 '방향' · Zephyr '타입' ·
    // GEN1 계열 '로더 통신 케이블' — packages/prisma/prisma/seed.ts MODELS).
    // 모델 레지스트리가 아직 안 왔거나 축이 없는 모델이면 종전 문구('구분')로 떨어진다.
    [trimOrNull(variantAxisLabel) ?? '구분', line.desiredDirection, r.variantValue],
    ['불감지', line.desiredSensor, r.sensorType],
  ];
  const checks: SpecAxisCheck[] = [];
  for (const [label, desiredRaw, actualRaw] of axes) {
    const desired = trimOrNull(desiredRaw);
    if (!desired) continue;
    // undefined = **서버가 그 축을 아예 안 실어 줬다**(구버전 응답). 그때는 대조하지
    // 않는다 — '우리가 모른다'를 '로봇에 값이 없다'로 바꿔 말하지 않기 위해서다.
    // null·빈 문자열은 서버가 "값이 없다"고 말해 준 것이므로 '사양 미입력'으로 센다.
    if (actualRaw === undefined) continue;
    const actual = trimOrNull(actualRaw);
    checks.push({
      label,
      desired,
      actual,
      state:
        actual === null ? 'unknown' : normSpec(actual) === normSpec(desired) ? 'match' : 'differ',
    });
  }
  return checks;
}

// 배지 툴팁 한 줄 — **실제 값**을 적는다. 종전 툴팁은 어긋난 축의 이름만 말해서
// ("계약 요청 사양과 다릅니다 (구분)") 무엇이 어떻게 다른지, 계약과 로봇 중 어느 쪽을
// 고쳐야 하는지를 화면만 보고는 알 수 없었다.
function axisCheckLine(x: SpecAxisCheck): string {
  return `${x.label}: 요청 ${x.desired} ↔ 로봇 ${x.actual ?? '미입력'}`;
}

// 개선계획 §2-3 — 서버가 stage 를 아직 안 내려주는 동안의 폴백. 배정이 끝난
// 계약은 handoffStatus 가 ASSIGNED_AWAITING_INSTALL 로 올라간다.
function contractStage(c: PendingContract): ContractAssignStage {
  return c.stage ?? (c.handoffStatus === 'ASSIGNED_AWAITING_INSTALL' ? 'assigned' : 'pending');
}

// 추가수정(26-07-27) §1-4 참고 — 배정 가능한 고객사 상태 3종.
// 출처: 서버 단일 진실원 apps/api/src/customer-master/customer-master.row.ts 의
// ASSIGNABLE_CUSTOMER_STATUSES (contracted / install_ready / install_done).
// api/assignments.ts:26 주석에도 같은 목록이 문서화돼 있다.
const ASSIGNABLE_CUSTOMER_STATUSES: readonly CustomerMasterStatus[] = [
  'contracted',
  'install_ready',
  'install_done',
];

function isKnownCustomerStatus(status: string | null | undefined): status is CustomerMasterStatus {
  return !!status && status in CUSTOMER_MASTER_STATUS_LABEL;
}

// 추가수정(26-07-27) §1-4 참고 — 카드 잠금(흐림 + 클릭 차단) 판정.
//
// stage 만 보면 안 되는 이유: 서버는 assigned 행에 assignable=false 를 '고정'으로
// 내려보내면서(assignments.service.ts, "더 붙일 로봇이 없으므로") customerStatus 는
// 사업팀 수동 override 를 존중해 그대로 내려준다. 그래서 stage 기준으로 잠금을
// 풀면 배정 후 '회수 예정'·'종료'로 바뀐 고객사까지 "설치 대기 — 로봇 교체 가능"
// 으로 보인다. 잠금이 풀리는 것은 배정 가능 상태 3종뿐이다.
//
// customerStatus 가 비었거나 모르는 값이면 기존 폴백(pending && !assignable).
function isContractGated(c: PendingContract): boolean {
  if (isKnownCustomerStatus(c.customerStatus)) {
    return !ASSIGNABLE_CUSTOMER_STATUSES.includes(c.customerStatus);
  }
  return contractStage(c) === 'pending' && !c.assignable;
}

// 카드를 펼쳤을 때 보여줄 배정 로봇 목록. 서버가 assignedRobots 를 주면 그것을,
// 아니면 reservedRobots 로 폴백한다(그 경우 status 를 몰라 교체를 막지 않는다).
//
// 추가수정(26-08-14) §2 — 사양 3축(색상·구분·불감지)과 modelId 를 **같은 응답의
// reservedRobots 에서 채운다.** 서버가 두 목록을 stage 별로 다르게 만들기 때문이다
// (assignments.service.ts): '배정 완료' 행은 assignedRobots = reservedRobots 라 사양이
// 다 들어 있지만, '배정 대기' 행은 assignedRobots 를 id·시리얼·상태 4칸으로만 만든다.
// 그래서 배정이 일부 끝난 대기 카드는 펼쳐도 옵션도, 어긋남 표시도 나오지 않았다 —
// 정작 같은 응답 안에 값이 들어 있는데도. 서버 select 를 늘리지 않는 이유는 그 응답
// 크기를 일부러 줄여 둔 주석이 거기 남아 있기 때문이다(붙일 수 있는 곳에서 붙인다).
function assignedRobotsOf(c: PendingContract): AssignedRobotRef[] {
  const specById = new Map((c.reservedRobots ?? []).map((r) => [r.id, r]));
  if (c.assignedRobots) {
    return c.assignedRobots.map((a) => {
      const spec = specById.get(a.id);
      // 짝을 못 찾으면 받은 그대로 둔다 — 없는 사양을 지어내지 않는다(그 로봇은
      // 옵션 없이 번호만 나오고 어긋남 판정도 하지 않는다).
      if (!spec) return a;
      // **서버가 안 보낸 칸(undefined)만** 채운다. `??` 로 쓰면 서버가 "이 로봇엔 값이
      // 없다"고 말한 null 까지 덮어써서, 사양을 아직 안 적은 로봇이 '값을 모르는 로봇'으로
      // 둔갑한다(그러면 '사양 미입력' 표시가 영영 안 뜬다).
      const fill = <T,>(sent: T | undefined, fallback: T | undefined): T | undefined =>
        sent === undefined ? fallback : sent;
      return {
        ...a,
        modelId: fill(a.modelId, spec.modelId),
        color: fill(a.color, spec.color),
        variantValue: fill(a.variantValue, spec.variantValue),
        sensorType: fill(a.sensorType, spec.sensorType),
      };
    });
  }
  return (c.reservedRobots ?? []).map((r) => ({
    id: r.id,
    serialShort: r.serialShort,
    serialLong: r.serialLong,
    modelId: r.modelId,
    color: r.color,
    variantValue: r.variantValue,
    sensorType: r.sensorType,
  }));
}

function robotLabel(r: { serialShort: string | null; serialLong: string | null; id: string }) {
  return r.serialShort ?? r.serialLong ?? r.id;
}

// 고객사 카드의 "로봇" 칸 — 모델별로 합쳐 "GEN2 × 1 · GEN1 × 2".
//
// 아래 세 함수가 빈 배열로 방어하는 이유: 서버가 stage 별로 서로 다른 select 를
// 쓴다. 응답에 없는 컬렉션을 그대로 순회하면 그 카드가 뜨는 순간 화면 전체가
// 죽는다(실제로 '배정 완료' 행에 lines 가 빠져 한 번 그렇게 됐다).
function formatModelSummary(lines: PendingContract['lines'] | undefined): string {
  const byModel = new Map<string, number>();
  for (const l of lines ?? []) {
    const label = l.model?.displayName || l.model?.code || l.modelId.slice(0, 6);
    byModel.set(label, (byModel.get(label) ?? 0) + l.quantity);
  }
  return [...byModel].map(([label, qty]) => `${label} × ${qty}`).join(' · ') || '—';
}

function requiredOf(c: PendingContract): number {
  return (c.lines ?? []).reduce((s, l) => s + l.quantity, 0);
}
function assignedOf(c: PendingContract): number {
  return (
    (c.reservedRobots ?? []).length +
    (c.productionAllocations ?? []).reduce((s, a) => s + a.allocatedCount, 0)
  );
}

// S2 Phase 2.5 — specMatch.tier → 배지 텍스트 + 색상.
const SPEC_BADGE: Record<
  SpecMatchTier,
  { label: string; iconName?: IconName; bg: string; fg: string }
> = {
  exact: {
    label: '완전일치',
    iconName: 'check',
    bg: 'var(--bg-success-subtle)',
    fg: 'var(--fg-on-success-tint)',
  },
  partial: {
    label: '부분일치',
    iconName: 'alert',
    bg: 'var(--bg-warning-subtle)',
    fg: 'var(--fg-on-warning-tint)',
  },
  mismatch: {
    label: '불일치',
    iconName: 'x',
    bg: 'var(--bg-danger-subtle)',
    fg: 'var(--severity-urgent)',
  },
  unspecified: { label: '미정', bg: 'var(--bg-subtle)', fg: 'var(--fg-muted)' },
};

// 개선계획 §2-2 — [완전일치만 보기] 가 켜져 있을 때 통과하는 tier.
// 'unspecified'(영업이 희망 사양을 아직 안 넣은 계약)까지 숨기면 필터가 켜진
// 기본 상태에서 후보가 통째로 사라진다 — 판정 불가는 '불일치'가 아니다.
function passesExactFilter(tier: SpecMatchTier): boolean {
  return tier === 'exact' || tier === 'unspecified';
}

/**
 * 추가수정(26-08-14) §2 — 서버가 내려주는 불일치 축 이름을 **그 모델의 말**로 바꾼다.
 *
 * 판정 정본(packages/shared/src/spec-match.ts)은 축 라벨이 고정 문자열('색상'/'방향'/
 * '불감지')이라 모델마다 다른 구분 축 이름을 모른다 — Zephyr 는 '타입'(기본/갈비),
 * GEN1 계열은 '로더 통신 케이블'(485/TTL)인데 화면이 '방향'이라고 부르고 있었다.
 * 정본을 고치지 않는 이유: 그 파일은 서버와 공유라 손대면 이 화면 밖으로 번진다.
 * 모델 축 이름을 모르면(레지스트리 도착 전·축이 없는 모델) 받은 그대로 둔다.
 */
function relabelVariantAxis(
  labels: string[],
  variantAxisLabel: string | null | undefined,
): string[] {
  const axis = trimOrNull(variantAxisLabel);
  if (!axis) return labels;
  return labels.map((l) => (l === '방향' ? axis : l));
}

function SpecMatchBadge({
  match,
  variantAxisLabel,
}: {
  match: SpecMatchResult;
  variantAxisLabel?: string | null;
}) {
  const cfg = SPEC_BADGE[match.tier];
  const mismatches = relabelVariantAxis(match.mismatches, variantAxisLabel);
  const title =
    match.tier === 'partial' || match.tier === 'mismatch'
      ? `불일치: ${mismatches.join(', ')}`
      : match.tier === 'exact'
        ? '희망 사양과 모두 일치합니다'
        : '희망 사양이 아직 없습니다';
  const label =
    match.tier === 'partial' && mismatches.length > 0
      ? `${cfg.label}(${mismatches.join('·')})`
      : cfg.label;
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: 10,
        padding: '1px 6px',
        borderRadius: 'var(--r-pill)',
        background: cfg.bg,
        color: cfg.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.iconName && <Icon name={cfg.iconName} size={11} aria-hidden />}
      {label}
    </span>
  );
}

// 개선 9건 차수2c(26-08-04) §5 — 자동 연결 중지 안내 배너(목록 머리).
const ambiguousBanner: CSSProperties = {
  margin: '8px 10px 0',
  padding: '8px 10px',
  fontSize: 11,
  lineHeight: 1.5,
  borderRadius: 6,
  background: 'var(--bg-warning-subtle)',
  border: '1px solid var(--border-warning-saturated)',
  color: 'var(--fg-default)',
};
const ellipsis: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// 배정이 필요한 고객사 정렬(2026-10-22 오너 "최신순, 이름순, 상태순 같은 게 있어야").
// 기본 '설치 예정순'은 서버가 내려준 순서(설치 급한 순 → 계약일) 그대로다.
type InboxSort = 'install' | 'latest' | 'name' | 'status';
const INBOX_SORTS: Array<{ value: InboxSort; label: string }> = [
  { value: 'install', label: '설치 예정순' },
  { value: 'latest', label: '최신순' },
  { value: 'name', label: '이름순' },
  { value: 'status', label: '상태순' },
];

/** 상태순 — 할 일 먼저: 배정 필요 → 배정 중 → 배정 완료 → 배정 불가(회수 예정 · 종료). 배지와 같은 판정. */
function statusRank(c: PendingContract): number {
  if (isContractGated(c)) return 3;
  const required = requiredOf(c);
  const assigned = assignedOf(c);
  if (required > 0 && assigned >= required) return 2;
  return assigned > 0 ? 1 : 0;
}

/** 같은 순위끼리는 서버 순서를 지킨다(Array.prototype.sort 는 안정 정렬). */
export function sortInboxItems(items: PendingContract[], sort: InboxSort): PendingContract[] {
  if (sort === 'install') return items;
  const out = items.slice();
  if (sort === 'name') {
    out.sort((a, b) => a.customer.displayName.localeCompare(b.customer.displayName, 'ko'));
  } else if (sort === 'status') {
    out.sort((a, b) => statusRank(a) - statusRank(b));
  } else {
    // 최신순 — 계약일 늦은 것 먼저, 계약일 없는 것은 뒤. 같으면 계약 번호 큰 것(나중 계약) 먼저.
    const t = (c: PendingContract) =>
      c.signedAt ? Date.parse(c.signedAt) : Number.NEGATIVE_INFINITY;
    out.sort((a, b) => t(b) - t(a) || b.humanId.localeCompare(a.humanId));
  }
  return out;
}

/** 교체 진행 상태 — release 는 성공했는데 재배정 전/실패인 계약을 표시한다. */
interface PendingReplacement {
  contractId: string;
  robotLabel: string;
}

export function AssignmentInboxPage() {
  const qc = useQueryClient();
  const toast = useToast();
  // 로봇 배정은 **운영 권한**(canEditMfg = canEditAny — 열람 전용만 제외)이면 가능하다.
  //
  // ⚠ 2026-09-11 정정 — 이 주석은 "제조팀 권한(ADMIN/MFG_MANAGER/MANAGER)" 이라고
  // 적혀 있었는데, 2026-08-27 권한 통합으로 `canEditMfg` 는 이미 `canEditAny` 의
  // 별칭이 되어 **필드팀 매니저를 통과시키고 있었다**(permissions.ts:45).
  // 그런데 서버 쪽 `requireMfgRole` 만 옛 목록에 남아 있어서, 필드팀 매니저에게는
  // **화면은 열리고 → 질의가 나가고 → 403 이 오는데 → 아래 목록이 "배정·설치 대기
  // 고객사가 없습니다" 로 그려졌다**(= 일감이 없는 것처럼 보임). 서버를 같은 배포에서
  // 정본(OPERATIONS_ROLES)으로 맞췄고, 아래 목록도 실패를 빈 목록과 구분해 말하도록 고쳤다.
  const role = useAuthStore((s) => s.user?.role);
  const canAssign = !!role && canEditMfg(role);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 2026-11-02 — 이벤트용 배정. 계약과 이벤트 중 하나만 고른다.
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selections, setSelections] = useState<AssignSelection[]>([]);
  // 개선계획 §2-2 — 기본 켜짐. state 초기값이라 페이지 재진입 시 항상 켜진다
  // (localStorage 로 기억하지 않는 것이 결정 사항).
  const [exactOnly, setExactOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<InboxSort>('install');
  const [replaceTarget, setReplaceTarget] = useState<{
    contractId: string;
    robot: AssignedRobotRef;
    /** 무대에서 배정된 로봇 위에 끌어 놓았으면 — 풀고 나서 이 로봇을 넣어 둔다. */
    incoming?: { id: string; label: string };
  } | null>(null);
  const [pendingReplacement, setPendingReplacement] = useState<PendingReplacement | null>(null);
  const stageRef = useRef<StageHandle>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  // 카드 띠는 한 줄로 옆으로 흐른다 — 세로 휠도 옆으로 굴린다(끝에 닿으면 페이지가 굴러가게 놓아 준다).
  useEffect(() => {
    const el = cardsRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX) || el.scrollWidth <= el.clientWidth) return;
      const atStart = el.scrollLeft <= 0 && e.deltaY < 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 && e.deltaY > 0;
      if (atStart || atEnd) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [canAssign]);

  const listQ = useQuery({
    queryKey: ['assignments', 'inbox'],
    queryFn: () => assignmentsApi.list(),
    staleTime: 30_000,
    // 권한 없는 역할에게는 403 네트워크 호출 자체를 막는다(아래 안내 화면 노출).
    enabled: canAssign,
  });

  // 2026-11-02 오너 — "로봇 배정도 이벤트용 배정 기능이 필요해". 캘린더에서 추가한 이벤트(회수 전, 출고할
  // 로봇이 있는 것)를 고객사 카드 앞에 따로 보인다.
  const eventsQ = useQuery({
    queryKey: ['assignments', 'events'],
    queryFn: () => scheduleDatesApi.listEvents(),
    staleTime: 30_000,
    enabled: canAssign,
  });

  // 추가수정(26-07-27) §1-4 — 선택된 계약의 stage. assigned(설치 대기) 계약은
  // 후보 질의 자체를 막는다 — 서버 candidates() 에는 assigned 게이트가 없어서
  // 후보가 실제로 내려오면 필요 수량이 찬 계약에 초과 배정 UI 가 생긴다.
  const selectedContract = (listQ.data?.items ?? []).find((c) => c.id === selectedId) ?? null;
  const selectedStage = selectedContract ? contractStage(selectedContract) : null;
  // 추가수정(26-07-27) §1-4 참고 — 선택 후 목록이 갱신되며 고객사 상태가 배정
  // 불가로 바뀐 경우까지 같은 판정으로 막는다(카드 잠금과 한 기준).
  const selectedGated = selectedContract ? isContractGated(selectedContract) : false;

  // '갇힌 행' 구제(26-07-30) — stage='assigned' 인데 **배정이 실제로 0대**인 계약은
  // 후보 조회를 열어 준다.
  //
  // 왜: 그 상태는 예약 0대로도 성립한다(제조 계획 배정만으로 전이 후 해제된 경우).
  // 종전 조건은 그런 행의 후보 질의를 막아, 화면에서 로봇을 붙일 수도 없고 목록에서
  // 빠지지도 않는 **죽은 카드**가 됐다(운영 사례: 한담 상동점). 서버 candidates() 에는
  // handoffStatus 게이트가 없어 응답은 원래부터 정상이다 — 막고 있던 건 이 한 줄이다.
  //
  // 위 주석의 원래 우려(초과 배정 UI)는 그대로 유효하므로 **부족분이 있을 때만** 연다.
  //
  // 개선 9건 차수1(26-08-04) §5 — 조건을 '배정 0대'에서 **부분 배정 전체**로 넓힌다.
  //
  // 왜: 0대만 구제한 조건이 슭돈 이대역본점 실사고를 놓쳤다. 필요 2 · 배정 1 인
  // '설치 대기' 행에서 배지는 '배정 중 1/2' 라고 옳게 말하는데 왼쪽 패널은 "필요
  // 수량을 모두 채웠습니다" 라는 **반대되는 거짓**을 말했고, 후보 질의까지 막혀
  // 모자란 1대를 붙일 길이 화면에 없었다. 숫자가 정본이다 — 부족하면 후보를 연다.
  //
  // 초과 배정 우려는 그대로 막힌다: 필요 ≤ 배정이면 종전대로 교체 안내만 남고
  // (제조 계획 배정으로 찬 행 포함 — assignedOf 가 그것을 함께 센다), 서버
  // candidates() 도 모델별 잔여만 내려준다. 필요 수량을 모르는 행(lines 미수신 →
  // required 0)은 종전 '배정 0대' 조건이 계속 담당한다.
  const selectedStuck =
    selectedStage === 'assigned' && selectedContract
      ? assignedOf(selectedContract) === 0 ||
        requiredOf(selectedContract) > assignedOf(selectedContract)
      : false;

  const candidatesQ = useQuery({
    queryKey: ['assignments', 'candidates', selectedId],
    queryFn: () => assignmentsApi.candidates(selectedId!),
    enabled: !!selectedId && !selectedGated && (selectedStage !== 'assigned' || selectedStuck),
  });

  // docs/work_log — fetch model registry to resolve modelId → displayName
  // for the remaining-count chips. Previously they showed `modelId.slice(0,6)+'…'`
  // — a meaningless hash prefix. Cached aggressively since models rarely
  // change inside a session.
  const modelsQ = useQuery({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    staleTime: 10 * 60_000,
  });
  const modelNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const md of modelsQ.data?.items ?? []) {
      m.set(md.id, md.displayName);
    }
    return m;
  }, [modelsQ.data]);
  // 추가수정(26-08-14) §2 — 모델별 '구분 사양' 축 이름. 배지 툴팁이 어긋난 축을 그
  // 모델의 말로 부르기 위한 것("타입: 요청 갈비 ↔ 로봇 기본"). 이미 받아 둔 같은
  // 레지스트리를 쓰므로 추가 조회는 없다. 축이 없는 모델(센서리스)은 null.
  const variantAxisLabelByModelId = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const md of modelsQ.data?.items ?? []) {
      m.set(md.id, md.variantAxisLabel ?? null);
    }
    return m;
  }, [modelsQ.data]);

  const assign = useMutation({
    mutationFn: ({ contractId, sels }: { contractId: string; sels: AssignSelection[] }) =>
      // 같은 계약에서 방금 해제가 있었으면 교체로 본다 — 서버가 완료 알림을
      // 다시 발행해야 필드팀이 바뀐 로봇 시리얼을 안다.
      assignmentsApi.assign(contractId, sels, false, pendingReplacement?.contractId === contractId),
    onSuccess: (_res, vars) => {
      // Toast API 시그니처 — 기존 `show(msg, 'success')` 가 `success(msg, title?)`
      // 로 리네이밍됨 (Toast.tsx:103). 두 번째 인자는 variant 가 아니라 title.
      toast.success('배정을 저장했습니다.');
      stageRef.current?.burst();
      qc.invalidateQueries({ queryKey: ['assignments'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'manufacturing'] });
      setSelections([]);
      // 교체 흐름의 재배정이 끝났으면 '해제됨 — 재배정 필요' 표시를 거둔다.
      if (pendingReplacement?.contractId === vars.contractId) setPendingReplacement(null);
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      // 교체 중 assign 이 실패하면 pendingReplacement 를 그대로 둔다 — 카드에
      // '해제됨 — 재배정 필요' 가 남아야 담당자가 빠뜨리지 않는다.
      toast.error(err.response?.data?.message ?? '배정하지 못했습니다.');
    },
  });

  const release = useMutation({
    mutationFn: ({
      contractId,
      robotId,
    }: {
      contractId: string;
      robotId: string;
      incoming?: { id: string; label: string };
    }) => assignmentsApi.release(contractId, robotId),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: ['assignments'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'manufacturing'] });
      setSelectedId(vars.contractId);
      // 무대에서 바꿀 로봇을 끌어 놓았으면 그 로봇을 넣어 둔 채로 — 저장만 누르면 된다.
      setSelections(vars.incoming ? [{ kind: 'stock', robotId: vars.incoming.id }] : []);
      toast.success(
        vars.incoming
          ? `배정을 해제하고 ${vars.incoming.label}을(를) 넣어 두었습니다. 저장하면 바뀝니다.`
          : '배정을 해제했습니다. 왼쪽에서 교체할 로봇을 골라 주세요.',
      );
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err.response?.data?.message ?? '배정을 해제하지 못했습니다.');
    },
  });

  const items = listQ.data?.items ?? [];
  const events = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = assignableEvents(eventsQ.data?.items);
    return !q
      ? all
      : all.filter(
          (e) => e.location.toLowerCase().includes(q) || e.content.toLowerCase().includes(q),
        );
  }, [eventsQ.data, search]);
  const selectedEvent = events.find((e) => e.id === selectedEventId) ?? null;
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const found = !q
      ? items
      : items.filter(
          (c) =>
            c.customer.displayName.toLowerCase().includes(q) ||
            c.customer.legalName.toLowerCase().includes(q),
        );
    return sortInboxItems(found, sort);
  }, [items, search, sort]);

  // ============================================================
  // 개선 9건 차수2c(26-08-04) §5 — 모호성 가드를 화면이 말한다
  // ============================================================
  //
  // 서버는 같은 고객사에 **배정 대기 계약이 2건 이상**이면 이미 나가 있는 로봇의 자동
  // 연결을 멈춘다(어느 계약 몫인지 기계가 확정할 수 없으므로 — contract-handoff.helper).
  // 그 멈춤은 지금까지 서버 로그에만 남아, 제조팀은 "왜 이 매장 로봇이 계약에 안 붙나"를
  // 알 방법이 없었다. 추가 판매를 새 계약으로 유도하면 이 상황이 늘어난다.
  //
  // 이 인박스 목록이 곧 그 판정의 재료다 — 서버가 보는 집합(ACTIVE + 배정 대기 3단계)과
  // **같은 목록**이므로, 같은 고객사가 2행 이상이면 그 고객사가 정확히 그 상황이다.
  // 그래서 별도 API 없이 여기서 세어 알린다(검색으로 걸러진 목록이 아니라 **전체**를
  // 센다 — 검색이 짝을 숨기면 경고가 사라져 버린다).
  //
  // ⚠ 목록 상한(hasMore)에 걸려 짝이 안 실려 온 고객사는 여기서 셀 수 없다. 그 경우
  //   경고가 없을 수 있다는 뜻이고, 아래 상한 안내가 이미 그 사실을 말한다.
  const awaitingCountByCustomer = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of items) m.set(c.customer.id, (m.get(c.customer.id) ?? 0) + 1);
    return m;
  }, [items]);
  const ambiguousCustomerCount = useMemo(
    () => [...awaitingCountByCustomer.values()].filter((n) => n > 1).length,
    [awaitingCountByCustomer],
  );

  const selectContract = (c: PendingContract) => {
    // 추가수정(26-07-27) §1-4 — toast 차단은 고객사 상태가 배정 불가일 때만
    // (isContractGated — stage 가 아니라 customerStatus 기준). 설치 대기라도
    // 상태가 배정 가능하면 클릭을 받아 무대에 배정된 로봇과 교체 단추를 띄운다(후보
    // 로딩은 candidatesQ enabled 에서 차단).
    if (isContractGated(c)) {
      toast.error(
        `현재 고객사 상태(${customerStatusLabel(c.customerStatus)})에서는 배정할 수 없습니다.`,
        '배정 불가',
      );
      return;
    }
    // 다른 계약으로 넘어가면 넣어 둔 로봇은 초기화 — 오배정 방지.
    if (selectedId !== c.id) setSelections([]);
    setSelectedEventId(null);
    setSelectedId(c.id);
  };

  if (!canAssign) {
    return (
      <div
        style={{
          maxWidth: 520,
          margin: '48px auto',
          padding: 24,
          textAlign: 'center',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-surface)',
          color: 'var(--fg-muted)',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg-default)', marginBottom: 8 }}>
          운영 권한이 필요합니다
        </div>
        로봇 배정은 열람 전용 계정으로는 할 수 없습니다. 재고 현황은 제조 그룹의 “재고 현황” 탭에서
        열람할 수 있습니다.
      </div>
    );
  }

  return (
    <div data-testid="assign-layout" className="asg-page">
      {/* 위: 배정할 고객사 · 이벤트 — 설치일이 큰 숫자로 서는 카드 띠(시안 5). */}
      <section data-testid="assign-contract-panel" className="asg-panel asg-strip">
        <div className="asg-panel-head">
          <span className="asg-panel-title">배정할 고객사 · 이벤트</span>
          <input
            type="search"
            aria-label="고객사명 검색"
            placeholder="고객사 · 이벤트 찾기"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="asg-field"
            style={{ width: 220 }}
          />
          <select
            aria-label="고객사 정렬"
            data-testid="assign-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as InboxSort)}
            className="asg-field"
            style={{ cursor: 'pointer' }}
          >
            {INBOX_SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <span className="asg-aside">
            고객사 {filteredItems.length}곳{events.length > 0 ? `, 이벤트 ${events.length}건` : ''}
          </span>
        </div>
        {/* 개선 9건 차수2c(26-08-04) §5 — 자동 연결이 멈춘 고객사가 있으면 목록 머리에서
            먼저 말한다. 카드 하나만 보고 있으면 "같은 고객사에 계약이 둘"이라는 사실이
            눈에 안 들어오기 때문이다. 판정 근거는 이 목록 자체(위 주석). */}
        {ambiguousCustomerCount > 0 && (
          <div data-testid="assign-ambiguous-banner" style={ambiguousBanner}>
            같은 고객사에 배정 대기 계약이 2건 이상인 고객사가 {ambiguousCustomerCount}곳 있습니다.
            이런 고객사는 어느 계약 몫인지 정할 수 없어서{' '}
            <strong>이미 나가 있는 로봇이 자동으로 연결되지 않습니다</strong>. 카드에서 계약을 골라
            직접 배정해 주세요.
          </div>
        )}
        <div ref={cardsRef} className="asg-cards" data-testid="assign-cards">
          {events.map((e) => (
            <EventStripCard
              key={e.id}
              event={e}
              selected={selectedEventId === e.id}
              onSelect={() => {
                setSelectedId(null);
                setSelections([]);
                setSelectedEventId(e.id);
              }}
            />
          ))}
          {filteredItems.length === 0 ? (
            <div className="asg-strip-empty">
              {/* 2026-09-11 — 종전에는 세 갈래뿐이라 **불러오기 실패(403·네트워크·서버 오류)가
                  "없습니다" 로 그려졌다.** 일감이 없는 것과 못 불러온 것은 다른 말이어야 한다. */}
              {listQ.isLoading
                ? '불러오는 중…'
                : listQ.isError
                  ? '목록을 불러오지 못했습니다. 권한이 없거나 서버에 연결하지 못했을 수 있습니다. 새로고침해도 같으면 관리자에게 알려 주세요.'
                  : items.length > 0
                    ? '검색 결과가 없습니다'
                    : '배정이나 설치를 기다리는 고객사가 없습니다'}
            </div>
          ) : (
            filteredItems.map((c) => (
              <ContractStripCard
                key={c.id}
                contract={c}
                selected={selectedId === c.id}
                onSelect={() => selectContract(c)}
                replacementPending={pendingReplacement?.contractId === c.id}
                // 개선 9건 차수2c(26-08-04) §5 — 같은 고객사의 다른 배정 대기 계약 수.
                // >0 이면 그 고객사는 자동 연결이 멈춘 상태다(카드가 그 사실을 적는다).
                siblingAwaitingCount={(awaitingCountByCustomer.get(c.customer.id) ?? 1) - 1}
              />
            ))
          )}
          {listQ.data?.hasMore && (
            <div className="asg-strip-empty" style={{ fontSize: 11 }}>
              고객사가 많아 일부만 보입니다. 검색으로 좁혀 주세요.
            </div>
          )}
        </div>
      </section>

      {/* 아래: 창고 로봇 | 무대 */}
      {selectedEvent ? (
        <EventBoard
          key={selectedEvent.id}
          event={selectedEvent}
          variantAxisLabelByModelId={variantAxisLabelByModelId}
        />
      ) : selectedContract ? (
        <ContractBoard
          key={selectedContract.id}
          contract={selectedContract}
          gated={selectedGated}
          full={selectedStage === 'assigned' && !selectedStuck}
          candidates={candidatesQ.data}
          candidatesLoading={candidatesQ.isLoading}
          candidatesError={!!candidatesQ.error}
          modelNameById={modelNameById}
          variantAxisLabelByModelId={variantAxisLabelByModelId}
          modelsLoading={modelsQ.isLoading}
          modelsError={!!modelsQ.error}
          onRetryModels={() => void modelsQ.refetch()}
          exactOnly={exactOnly}
          onExactOnly={setExactOnly}
          selections={selections}
          onChange={setSelections}
          onAssign={() => assign.mutate({ contractId: selectedContract.id, sels: selections })}
          isAssigning={assign.isPending}
          onReplace={(robot, incoming) =>
            setReplaceTarget({ contractId: selectedContract.id, robot, incoming })
          }
          replacementPending={pendingReplacement?.contractId === selectedContract.id}
          siblingAwaitingCount={
            (awaitingCountByCustomer.get(selectedContract.customer.id) ?? 1) - 1
          }
          stageRef={stageRef}
        />
      ) : (
        <div className="asg-board" data-testid="assign-board">
          <PickPanel
            count={0}
            pickedCount={0}
            onPutPicked={() => {}}
            keyword=""
            onKeyword={() => {}}
            showTools={false}
          >
            <div className="asg-pick-note">위에서 고객사나 이벤트를 고르세요.</div>
          </PickPanel>
          <AssignStage
            testId="assign-stage"
            canDrop={false}
            dragging={false}
            flyRequest={null}
            lines={[]}
            onDrop={() => {}}
            header={null}
            empty={
              <div className="asg-floor-empty">
                고객사를 고르면 필요한 대수만큼 로봇 자리가 여기에 섭니다.
              </div>
            }
          />
        </div>
      )}

      <ConfirmDialog
        open={replaceTarget !== null}
        title="배정 로봇 교체"
        message={
          `${replaceTarget ? robotLabel(replaceTarget.robot) : ''}의 배정을 해제합니다.\n\n` +
          (replaceTarget?.incoming
            ? `해제한 자리에 ${replaceTarget.incoming.label}을(를) 넣어 둡니다. 저장하면 바뀝니다. `
            : '해제 후 왼쪽 목록에서 교체할 로봇을 골라 다시 배정해 주세요. ') +
          '다시 배정하기 전까지 카드에 "해제됨, 다시 배정해 주세요"로 남습니다.'
        }
        confirmLabel="해제하고 교체"
        danger
        busy={release.isPending}
        onConfirm={async () => {
          if (!replaceTarget) return;
          const target = replaceTarget;
          setReplaceTarget(null);
          setPendingReplacement({
            contractId: target.contractId,
            robotLabel: robotLabel(target.robot),
          });
          try {
            await release.mutateAsync({
              contractId: target.contractId,
              robotId: target.robot.id,
              incoming: target.incoming,
            });
          } catch {
            // 해제 자체가 실패했으면 교체가 시작되지도 않은 것 — 표시를 되돌린다.
            setPendingReplacement(null);
          }
        }}
        onCancel={() => setReplaceTarget(null)}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// 위: 고객사 카드
// ────────────────────────────────────────────────────────────────────────

// 배지는 **항상** 렌더한다(오너 제보 26-07-29 ②). 종전엔 배정 0대인 행에서 null 을
// 반환했는데, 카드마다 자기 grid 라 배지 칸(auto)이 0폭으로 접혀 **행마다 펼침 화살표
// 위치가 어긋났다**. 배지가 늘 있으면 칸이 접히지 않고, 그 행이 무엇을 기다리는지도
// 글자로 남는다.
//
// done 판정에서 `stage === 'assigned'` 를 뺀 이유(같은 제보 ①):
// 서버는 handoffStatus=ASSIGNED_AWAITING_INSTALL 이면 stage='assigned' 로 내려주는데,
// 그 상태는 **예약 로봇 0대로도 성립한다** — recomputeHandoffForAssignment 가
// `reservedCount + allocatedNow >= totalRequired` 로 판정하므로 제조 계획 배정만으로도
// 전이하고, 그 뒤 배정이 해제되면 0대인 채 상태만 남는다. 종전 판정은 그런 행에
// '0/1' 과 '배정 완료' 를 **동시에** 띄웠다(운영 실제 사례: 한담 상동점 CNT-2026-0003).
// 숫자가 정본이다 — 배정된 게 없으면 '배정 필요'로 말한다.
function StageBadge({ assigned, required }: { assigned: number; required: number }) {
  const done = required > 0 && assigned >= required;
  const label = done ? '배정 완료' : assigned > 0 ? `배정 중 ${assigned}/${required}` : '배정 필요';
  const tone = done
    ? { background: 'var(--bg-success-subtle)', color: 'var(--fg-on-success-tint)' }
    : assigned > 0
      ? { background: 'var(--bg-warning-subtle)', color: 'var(--fg-on-warning-tint)' }
      : // '배정 필요'는 경보가 아니라 할 일 — 중립 톤으로 둔다(경고색을 쓰면
        // 목록 대부분이 붉어져 진짜 부분배정 행이 묻힌다).
        { background: 'var(--bg-subtle)', color: 'var(--fg-muted)' };
  return (
    <span
      data-testid="assign-stage-badge"
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '1px 6px',
        borderRadius: 'var(--r-pill)',
        whiteSpace: 'nowrap',
        ...tone,
      }}
    >
      {label}
    </span>
  );
}

function ContractStripCard({
  contract: c,
  selected,
  onSelect,
  replacementPending,
  siblingAwaitingCount = 0,
}: {
  contract: PendingContract;
  selected: boolean;
  onSelect: () => void;
  replacementPending: boolean;
  /**
   * 개선 9건 차수2c(26-08-04) §5 — 같은 고객사의 **다른** 배정 대기 계약 수.
   * >0 이면 서버의 자동 연결(이미 나가 있는 로봇 ↔ 계약)이 이 고객사에서 멈춘다.
   * 기본 0 — 값을 주지 않는 호출부가 생겨도 종전 화면과 같다.
   */
  siblingAwaitingCount?: number;
}) {
  const required = requiredOf(c);
  const assigned = assignedOf(c);
  const stage = contractStage(c);
  // 추가수정(26-07-27) §1-4 — 잠금(흐림 + 클릭 차단)은 고객사 상태가 배정 불가일
  // 때만(isContractGated). 설치 대기(assigned)라도 상태가 배정 가능하면 정상 표시
  // — 고르면 로봇을 교체할 수 있는데 잠긴 것처럼 보여 수정 불가로 오해받던 것을
  // 푼다. 반대로 배정 후 '회수 예정'·'종료'로 바뀐 고객사는 계속 잠긴다.
  const gated = isContractGated(c);
  // 추가수정(26-07-27) §1-3 — 담당 칸: 고객사 측 담당자(primaryContactName)
  // 대신 우리 회사 담당 영업. 빈 값은 줄표.
  const salesOwner = c.customer.salesOwnerCode?.trim() || '—';
  const modelSummary = formatModelSummary(c.lines);
  const ambiguousText = `이 고객사에는 배정 대기 계약이 ${siblingAwaitingCount + 1}건 있어서 이미 나가 있는 로봇이 자동으로 연결되지 않습니다. 직접 배정해 주세요.`;

  return (
    <StripCard
      testId="assign-contract-card"
      headerTestId="assign-contract-header"
      selected={selected}
      gated={gated}
      onSelect={onSelect}
      date={c.scheduledInstallAt}
      done={required > 0 && assigned >= required}
      name={
        <Tooltip
          content={c.customer.legalName || c.customer.displayName}
          style={{ minWidth: 0, overflow: 'hidden' }}
        >
          <span style={{ display: 'block', ...ellipsis }}>{c.customer.displayName}</span>
        </Tooltip>
      }
      sub={
        <>
          <span title={modelSummary}>{modelSummary}</span>
          {' · '}
          <span title="담당 영업">{salesOwner}</span>
        </>
      }
      assigned={assigned}
      required={required}
      badge={<StageBadge assigned={assigned} required={required} />}
      notes={
        <>
          {gated && (
            <div className="asg-card-note">
              {customerStatusLabel(c.customerStatus)} 상태라 배정할 수 없습니다.
            </div>
          )}
          {replacementPending && (
            <div className="asg-card-note" data-tone="bad" data-testid="assign-replacement-pending">
              해제됨, 다시 배정해 주세요
            </div>
          )}
          {/* 개선 9건 차수2c(26-08-04) §5 — 이 고객사는 자동 연결이 멈춘 상태다.
              "조용히 멈추지 말고 화면에 알린다"(계획서 §1) — 배정 자체는 여기서 손으로 할 수
              있으므로 카드를 잠그지 않고 사실과 다음 행동만 적는다. */}
          {siblingAwaitingCount > 0 && (
            <div
              className="asg-card-note"
              data-tone="warn"
              data-testid="assign-ambiguous-note"
              title={ambiguousText}
            >
              {ambiguousText}
            </div>
          )}
          {/* 추가수정(26-07-27) §1-4 — 설치 대기는 '배정 불가'가 아니라 교체 가능 안내.
              단 잠긴 카드(회수 예정·종료 등)는 위의 '배정 불가' 안내만 남긴다 —
              두 문구가 같이 뜨면 서로 반대되는 말을 하게 된다.

              오너 제보(26-07-29 ①) — `assigned > 0` 조건. stage='assigned' 는 예약
              로봇 0대로도 성립하는데, 그 행에 "로봇 교체 가능"을 띄우면 **교체할 로봇이
              없는데 교체하라**는 말이 된다. 숫자가 정본이다. */}
          {stage === 'assigned' && !gated && assigned > 0 && !replacementPending && (
            <div className="asg-card-note">설치 대기 중입니다. 로봇을 바꿀 수 있습니다.</div>
          )}
        </>
      }
    />
  );
}

// ────────────────────────────────────────────────────────────────────────
// 아래: 고객사를 골랐을 때의 장착대 — 왼쪽 창고 로봇 · 가운데 무대
// ────────────────────────────────────────────────────────────────────────

type StockRobot = AssignmentCandidates['candidates']['stockRobots'][number];
type PlanCandidate = AssignmentCandidates['candidates']['productionPlans'][number];

const desiredOf = (l: ContractLine) => ({
  color: l.desiredColor ?? null,
  direction: l.desiredDirection ?? null,
  sensor: l.desiredSensor ?? null,
});
const hasDesired = (l: ContractLine) =>
  !!(trimOrNull(l.desiredColor) || trimOrNull(l.desiredDirection) || trimOrNull(l.desiredSensor));
const fits = (tier: SpecMatchTier) => tier === 'exact' || tier === 'unspecified';
const toneOf = (tier: SpecMatchTier): SlotTone =>
  tier === 'partial' ? 'warn' : tier === 'mismatch' ? 'bad' : 'ok';
const hintOf = (tier: SpecMatchTier): LineHint =>
  tier === 'partial' ? 'warn' : tier === 'mismatch' ? 'bad' : 'ok';

/** 줄의 희망 사양 칩 — 색상 · 구분 · 불감지. 하나도 없으면 '사양 지정 없음'. */
function DesiredSpec({ line }: { line: ContractLine }) {
  const words = [
    trimOrNull(line.desiredColor),
    trimOrNull(line.desiredDirection),
    trimOrNull(line.desiredSensor) ? `불감지 ${trimOrNull(line.desiredSensor)}` : null,
  ].filter((w): w is string => !!w);
  return (
    <span className="asg-spec">
      {words.length > 0 ? words.map((w) => <i key={w}>{w}</i>) : <i data-none>사양 지정 없음</i>}
    </span>
  );
}

function fullDate(iso: string | null): string {
  const ymd = iso ? kstYmdHyphen(iso) : null;
  if (!ymd) return '미정';
  const dow = '일월화수목금토'[new Date(`${ymd}T00:00:00Z`).getUTCDay()];
  return `${Number(ymd.slice(5, 7))}월 ${Number(ymd.slice(8, 10))}일 ${dow}요일`;
}

function ContractBoard({
  contract: c,
  gated,
  full,
  candidates: data,
  candidatesLoading,
  candidatesError,
  modelNameById,
  variantAxisLabelByModelId,
  modelsLoading,
  modelsError,
  onRetryModels,
  exactOnly,
  onExactOnly,
  selections,
  onChange,
  onAssign,
  isAssigning,
  onReplace,
  replacementPending,
  siblingAwaitingCount,
  stageRef,
}: {
  contract: PendingContract;
  gated: boolean;
  /** 필요 수량이 찬 설치 대기 행 — 후보 대신 교체 안내. */
  full: boolean;
  candidates: AssignmentCandidates | undefined;
  candidatesLoading: boolean;
  candidatesError: boolean;
  modelNameById: Map<string, string>;
  /** 추가수정(26-08-14) §2 — 불일치 배지가 축을 그 모델의 말로 부르기 위한 것. */
  variantAxisLabelByModelId: Map<string, string | null>;
  modelsLoading: boolean;
  modelsError: boolean;
  onRetryModels: () => void;
  exactOnly: boolean;
  onExactOnly: (v: boolean) => void;
  selections: AssignSelection[];
  onChange: (s: AssignSelection[]) => void;
  onAssign: () => void;
  isAssigning: boolean;
  onReplace: (robot: AssignedRobotRef, incoming?: { id: string; label: string }) => void;
  replacementPending: boolean;
  siblingAwaitingCount: number;
  stageRef: RefObject<StageHandle>;
}) {
  const toast = useToast();
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [dragLoad, setDragLoad] = useState<DragLoad | null>(null);
  const [fly, setFly] = useState<FlyRequest | null>(null);
  const [keyword, setKeyword] = useState('');
  // round5 #2c — 사양 불일치 배정 소프트 가드(경고+확인) 다이얼로그.
  // 개선계획 §2-2 — 사양별 수량 초과도 같은 창에서 함께 확인한다.
  const [softWarnings, setSoftWarnings] = useState<{
    mismatched: string[];
    specOverflow: string[];
  } | null>(null);

  const modelLabelOf = (modelId: string, fallback?: string | null): string => {
    const resolved = modelNameById.get(modelId);
    if (modelsLoading) return '...';
    return resolved ?? fallback ?? `${modelId.slice(0, 6)}…`;
  };

  const lines = c.lines ?? [];
  const allStock = useMemo(() => data?.candidates.stockRobots ?? [], [data]);
  const allPlans = useMemo(() => data?.candidates.productionPlans ?? [], [data]);
  const stockById = useMemo(() => new Map(allStock.map((r) => [r.id, r])), [allStock]);
  const planById = useMemo(() => new Map(allPlans.map((p) => [p.id, p])), [allPlans]);
  const canPlace = !!data && !gated && !full;

  const draftRobotIds = selections
    .filter((s) => s.kind === 'stock' && s.robotId)
    .map((s) => s.robotId!);
  const planCount = (planId: string, sels: AssignSelection[] = selections): number =>
    sels.find((s) => s.kind === 'plan' && s.productionPlanId === planId)?.count ?? 0;
  /** 이 모델에 넣어 둔(저장 전) 대수 — 재고 + 제조 계획. */
  const draftedFor = (modelId: string, sels: AssignSelection[]): number =>
    sels.reduce((n, s) => {
      if (s.kind === 'stock') return n + (stockById.get(s.robotId!)?.modelId === modelId ? 1 : 0);
      return n + (planById.get(s.productionPlanId!)?.modelId === modelId ? (s.count ?? 0) : 0);
    }, 0);
  const roomFor = (modelId: string, sels: AssignSelection[]): number =>
    (data?.remaining[modelId] ?? 0) - draftedFor(modelId, sels);
  const robotName = (r: { serialShort: string | null; serialLong: string | null; id: string }) =>
    robotLabel(r);

  // ── 무대 줄 · 자리 ───────────────────────────────────────────────────
  //
  // 서버는 로봇을 계약 '줄'이 아니라 계약에 붙이고, 줄 귀속은 사양으로 정한다(pairRobotsToLines 주석).
  // 그래서 자리를 고르는 것은 사람이 아니라 이 짝짓기다 — 어느 빈자리에 놓든 넣어 둔 로봇은 가장 잘
  // 맞는 줄에 선다. 배정된 로봇은 먼저 제자리에 고정하고, 넣어 둔 로봇은 남은 자리로만 짝짓는다
  // (넣을 때마다 배정된 로봇이 줄을 옮겨 다니지 않게).
  const robots = assignedRobotsOf(c);
  const pairedLines = pairRobotsToLines(robots, c.lines);
  const savedBy = new Map<string, AssignedRobotRef[]>(lines.map((l) => [l.id, []]));
  for (const r of robots) {
    // 모델을 모르는 로봇(구버전 서버 응답)은 짝이 없다 — 첫 줄에 같이 세워 둔다(숨기지 않는다).
    const l = pairedLines.get(r.id) ?? lines[0];
    if (l) savedBy.get(l.id)!.push(r);
  }
  const used = new Map(lines.map((l) => [l.id, savedBy.get(l.id)!.length]));
  const allocBy = new Map<
    string,
    Array<{ key: string; humanId: string | null; eta: string | null }>
  >(lines.map((l) => [l.id, []]));
  (c.productionAllocations ?? []).forEach((a, ai) => {
    const modelId = a.productionPlan?.modelId;
    for (let i = 0; i < a.allocatedCount; i += 1) {
      const same = lines.filter((l) => !modelId || l.modelId === modelId);
      const l = same.find((x) => used.get(x.id)! < x.quantity) ?? same[0] ?? lines[0];
      if (!l) continue;
      used.set(l.id, used.get(l.id)! + 1);
      allocBy.get(l.id)!.push({
        key: `alloc:${a.id ?? ai}:${i}`,
        humanId: a.productionPlan?.humanId ?? null,
        eta: a.productionPlan?.estimatedCompletionAt ?? null,
      });
    }
  });
  const draftRobots = draftRobotIds
    .map((id) => stockById.get(id))
    .filter((r): r is StockRobot => !!r);
  const draftPaired = pairRobotsToLines(
    draftRobots,
    lines.map((l) => ({ ...l, quantity: Math.max(0, l.quantity - used.get(l.id)!) })),
  );
  const draftBy = new Map<string, StockRobot[]>(lines.map((l) => [l.id, []]));
  for (const r of draftRobots) {
    const l = draftPaired.get(r.id) ?? lines.find((x) => x.modelId === r.modelId) ?? lines[0];
    if (!l) continue;
    draftBy.get(l.id)!.push(r);
    used.set(l.id, used.get(l.id)! + 1);
  }
  const planDraftBy = new Map<string, PlanCandidate[]>(lines.map((l) => [l.id, []]));
  for (const s of selections) {
    if (s.kind !== 'plan') continue;
    const p = planById.get(s.productionPlanId!);
    if (!p) continue;
    for (let i = 0; i < (s.count ?? 0); i += 1) {
      const same = lines
        .filter((l) => l.modelId === p.modelId)
        .sort(
          (a, b) =>
            TIER_RANK[matchRobotToDesiredSpec(desiredOf(a), p).tier] -
            TIER_RANK[matchRobotToDesiredSpec(desiredOf(b), p).tier],
        );
      const l = same.find((x) => used.get(x.id)! < x.quantity) ?? same[0];
      if (!l) continue;
      planDraftBy.get(l.id)!.push(p);
      used.set(l.id, used.get(l.id)! + 1);
    }
  }
  const emptyBy = new Map(lines.map((l) => [l.id, Math.max(0, l.quantity - used.get(l.id)!)]));

  // 끌고 있는 것 — 줄마다 받을 만한지(모델 · 사양)를 미리 보인다.
  const dragged: { modelId: string; spec: Parameters<typeof matchRobotToDesiredSpec>[1] } | null =
    (() => {
      const r = dragLoad?.robotIds.map((id) => stockById.get(id)).find(Boolean);
      if (r) return { modelId: r.modelId, spec: r };
      const p = dragLoad?.planIds.map((id) => planById.get(id)).find(Boolean);
      return p ? { modelId: p.modelId, spec: p } : null;
    })();

  function putPicked(): void {
    if (picked.size > 0) put([...picked], chipFrom());
  }

  const stageLines: StageLine[] = lines.map((l) => {
    const modelLabel = l.model?.displayName || l.model?.code || modelLabelOf(l.modelId);
    const axis = variantAxisLabelByModelId.get(l.modelId);
    const saved = savedBy.get(l.id)!;
    const filled =
      saved.length +
      allocBy.get(l.id)!.length +
      draftBy.get(l.id)!.length +
      planDraftBy.get(l.id)!.length;
    return {
      key: l.id,
      modelCode: l.model?.code ?? null,
      heading: lineHeading(l.model?.code ?? modelLabel, l.desiredDirection, axis),
      title: modelLabel,
      spec: <DesiredSpec line={l} />,
      count: `${filled}/${l.quantity}대`,
      hint: dragged
        ? dragged.modelId !== l.modelId
          ? 'off'
          : hintOf(matchRobotToDesiredSpec(desiredOf(l), dragged.spec).tier)
        : undefined,
      slots: [
        ...saved.map((r) => savedRobotSlot(r, l, modelLabel, axis)),
        ...allocBy.get(l.id)!.map((a) => ({
          key: a.key,
          kind: 'plan' as const,
          plate: (
            <span className="asg-sn" data-plain>
              {a.humanId ?? '제조 계획'}
            </span>
          ),
          sub: `완성 예정 ${kstYmd(a.eta) ?? '–'}`,
          testId: 'assign-assigned-plan',
        })),
        ...draftBy.get(l.id)!.map((r) => {
          const m = matchRobotToDesiredSpec(desiredOf(l), r);
          const spec = formatRobotSpec(r, modelLabel);
          return {
            key: `draft:${r.id}`,
            kind: 'robot' as const,
            draft: true,
            tone: toneOf(m.tier),
            flyKey: `robot:${r.id}`,
            plate: <span className="asg-sn">{robotName(r)}</span>,
            sub: spec ?? '사양 미입력',
            extra: !fits(m.tier) ? <SpecMatchBadge match={m} variantAxisLabel={axis} /> : null,
            testId: 'assign-draft-robot',
            action: (
              <button
                type="button"
                className="asg-fig-x"
                aria-label={`${robotName(r)} 빼기`}
                title="빼기"
                onClick={() =>
                  onChange(selections.filter((s) => !(s.kind === 'stock' && s.robotId === r.id)))
                }
              >
                <Icon name="x" size={13} aria-hidden />
              </button>
            ),
          };
        }),
        ...planDraftBy.get(l.id)!.map((p, i) => ({
          key: `plan-draft:${p.id}:${i}`,
          kind: 'plan' as const,
          draft: true,
          tone: p.isFeasible ? ('plain' as const) : ('bad' as const),
          flyKey: `plan:${p.id}`,
          plate: (
            <span className="asg-sn" data-plain>
              {p.humanId}
            </span>
          ),
          sub: `완성 예정 ${kstYmd(p.estimatedCompletionAt) ?? '–'}${p.isFeasible ? '' : ', 설치일보다 늦음'}`,
          action: (
            <button
              type="button"
              className="asg-fig-x"
              aria-label={`${p.humanId} 한 대 빼기`}
              title="빼기"
              onClick={() => onChange(withPlanCount(selections, p.id, planCount(p.id) - 1))}
            >
              <Icon name="x" size={13} aria-hidden />
            </button>
          ),
        })),
        ...Array.from({ length: emptyBy.get(l.id)! }, (_, i) => ({
          key: `empty:${l.id}:${i}`,
          kind: 'empty' as const,
          sub: hasDesired(l)
            ? [
                l.desiredColor,
                l.desiredDirection,
                l.desiredSensor ? `불감지 ${l.desiredSensor}` : null,
              ]
                .map((w) => trimOrNull(w))
                .filter(Boolean)
                .join(' ')
            : '사양 지정 없음',
          title: modelLabel,
          onClick: canPlace ? putPicked : undefined,
        })),
      ],
    };
  });

  function savedRobotSlot(
    r: AssignedRobotRef,
    line: ContractLine,
    modelLabel: string,
    axis: string | null | undefined,
  ): StageSlot {
    const deployed = r.status === 'DEPLOYED';
    // 옵션 표기 + 계약 요청 사양과의 어긋남(26-07-30). 표기는 왼쪽 재고 목록과 같은 형식.
    const spec = formatRobotSpec(r, modelLabel);
    // 추가수정(26-08-14) §2 — 대조 상대는 **이 로봇이 채우고 있는 줄**이다(pairRobotsToLines 주석).
    const checks = specAxisChecks(
      r,
      pairedLines.get(r.id) ?? null,
      r.modelId ? variantAxisLabelByModelId.get(r.modelId) : axis,
    );
    const differs = checks.filter((x) => x.state === 'differ');
    const unknowns = checks.filter((x) => x.state === 'unknown');
    const btn = (
      <button
        type="button"
        className="asg-fig-swap"
        disabled={deployed}
        onClick={() => onReplace(r)}
      >
        교체
      </button>
    );
    return {
      key: `saved:${r.id}`,
      kind: 'robot',
      tone: differs.length > 0 ? 'warn' : unknowns.length > 0 ? 'plain' : 'ok',
      replaceable: !deployed && canPlace,
      plate: <span className="asg-sn">{robotLabel(r)}</span>,
      title: r.serialLong ?? undefined,
      testId: 'assign-assigned-robot',
      sub: spec ? (
        <span data-testid="assign-robot-spec" title={spec}>
          {spec}
        </span>
      ) : null,
      extra: (
        <span
          style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}
        >
          {/* 어긋난 축이 하나라도 있으면 '요청과 다름'이 이긴다 — 값이 다르다는
              사실이 값이 비어 있다는 사실보다 급하다. 툴팁에는 두 종류를 모두
              적어 이 로봇에 대해 아는 것을 한 번에 보여준다. */}
          {differs.length > 0 ? (
            <span
              data-testid="assign-robot-spec-mismatch"
              title={[
                '계약 요청 사양과 다릅니다',
                ...[...differs, ...unknowns].map((x) => `· ${axisCheckLine(x)}`),
              ].join('\n')}
              style={{
                ...flagStyle,
                background: 'var(--bg-warning-subtle)',
                color: 'var(--fg-on-warning-tint)',
              }}
            >
              요청과 다름
            </span>
          ) : (
            unknowns.length > 0 && (
              <span
                data-testid="assign-robot-spec-unknown"
                title={[
                  '이 로봇에 적히지 않은 사양이 있어 계약 요청과 비교할 수 없습니다. 로봇 상세에서 채워 주세요.',
                  ...unknowns.map((x) => `· ${axisCheckLine(x)}`),
                ].join('\n')}
                style={{ ...flagStyle, background: 'var(--bg-subtle)', color: 'var(--fg-muted)' }}
              >
                사양 미입력
              </span>
            )
          )}
          {r.status && (
            <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
              {robotStatusLabel(r.status)}
            </span>
          )}
        </span>
      ),
      action: deployed ? (
        <Tooltip
          content="이미 출고된 로봇입니다. 바꾸려면 먼저 회수 처리해 주세요."
          focusable={false}
        >
          {btn}
        </Tooltip>
      ) : (
        btn
      ),
    };
  }

  // ── 넣기 ───────────────────────────────────────────────────────────

  /** 고른(끈) 로봇 · 제조 계획을 넣어 둔다. 자리가 없는 모델 · 늦는 계획은 빼고 알린다. */
  function put(chipIds: string[], from: FlyRequest['from']): void {
    if (!canPlace) return;
    let next = [...selections];
    const targets: FlyRequest['targets'] = [];
    let noRoom = 0;
    let late = 0;
    for (const chip of chipIds) {
      if (chip.startsWith('r:')) {
        const r = stockById.get(chip.slice(2));
        if (!r || next.some((s) => s.kind === 'stock' && s.robotId === r.id)) continue;
        if (roomFor(r.modelId, next) <= 0) {
          noRoom += 1;
          continue;
        }
        next = [...next, { kind: 'stock', robotId: r.id }];
        targets.push({ flyKey: `robot:${r.id}`, label: robotName(r) });
      } else if (chip.startsWith('p:')) {
        const p = planById.get(chip.slice(2));
        if (!p) continue;
        if (!p.isFeasible) {
          late += 1;
          continue;
        }
        if (planCount(p.id, next) >= p.availableCount || roomFor(p.modelId, next) <= 0) {
          noRoom += 1;
          continue;
        }
        next = withPlanCount(next, p.id, planCount(p.id, next) + 1);
        targets.push({ flyKey: `plan:${p.id}`, label: p.humanId });
      }
    }
    onChange(next);
    setPicked(new Set());
    if (targets.length > 0) setFly({ seq: Date.now(), from, targets });
    if (noRoom > 0)
      toast.error(
        `더 넣을 자리가 없어 ${noRoom}대는 빼 두었습니다. 바꾸려면 배정된 로봇 위에 놓으세요.`,
      );
    if (late > 0)
      toast.error('완성 예정일이 설치일보다 늦은 제조 계획은 여기서 배정할 수 없습니다.');
  }

  /**
   * 맞는 로봇으로 채우기 — 빈자리마다 희망 사양에 맞는 재고(없으면 늦지 않는 제조 계획)를 넣어 둔다.
   * 희망 사양이 있는 줄을 먼저 채운다(사양 없는 줄이 맞는 로봇을 먼저 가져가지 않게). 같은 사양이면
   * 계약과 같은 법인 → 신규 → 출하 준비 → 번호순. 사양이 다른 로봇은 넣지 않는다 — 그건 사람이 고른다.
   */
  function fill(): void {
    if (!data) return;
    let next = [...selections];
    const targets: FlyRequest['targets'] = [];
    const own = data.contract.ownerEntity;
    for (const l of [...lines].sort((a, b) => Number(hasDesired(b)) - Number(hasDesired(a)))) {
      let room = Math.min(emptyBy.get(l.id) ?? 0, roomFor(l.modelId, next));
      if (room <= 0) continue;
      const pool = allStock
        .filter(
          (r) =>
            r.modelId === l.modelId && !next.some((s) => s.kind === 'stock' && s.robotId === r.id),
        )
        .map((r) => ({ r, m: matchRobotToDesiredSpec(desiredOf(l), r) }))
        .filter((x) => fits(x.m.tier))
        .sort(
          (a, b) =>
            TIER_RANK[a.m.tier] - TIER_RANK[b.m.tier] ||
            Number(!!a.r.ownerEntity && a.r.ownerEntity !== own) -
              Number(!!b.r.ownerEntity && b.r.ownerEntity !== own) ||
            Number(!!b.r.isNew) - Number(!!a.r.isNew) ||
            Number(b.r.status === 'READY_TO_SHIP') - Number(a.r.status === 'READY_TO_SHIP') ||
            robotName(a.r).localeCompare(robotName(b.r), 'ko', { numeric: true }),
        );
      for (const { r } of pool) {
        if (room <= 0) break;
        next = [...next, { kind: 'stock', robotId: r.id }];
        targets.push({ flyKey: `robot:${r.id}`, label: robotName(r) });
        room -= 1;
      }
      for (const p of allPlans) {
        if (room <= 0) break;
        if (p.modelId !== l.modelId || !p.isFeasible) continue;
        if (!fits(matchRobotToDesiredSpec(desiredOf(l), p).tier)) continue;
        const take = Math.min(room, p.availableCount - planCount(p.id, next));
        if (take <= 0) continue;
        next = withPlanCount(next, p.id, planCount(p.id, next) + take);
        for (let i = 0; i < take; i += 1)
          targets.push({ flyKey: `plan:${p.id}`, label: p.humanId });
        room -= take;
      }
    }
    if (targets.length === 0) {
      toast.error('희망 사양에 맞는 로봇이 없습니다. 다른 사양은 직접 골라 주세요.');
      return;
    }
    const pts = chipCenters();
    onChange(next);
    setPicked(new Set());
    setFly({ seq: Date.now(), from: (label) => pts.get(label) ?? null, targets });
  }

  // ── 저장 전 확인 ─────────────────────────────────────────────────────

  const selectedStock = draftRobots;
  // 개선계획 §2-2 — 모델별 수량 초과는 배정 버튼을 누르기 전에 막는다(하드 차단).
  const overflowWarnings = useMemo(() => {
    const pickedBy = new Map<string, number>();
    for (const r of selectedStock) pickedBy.set(r.modelId, (pickedBy.get(r.modelId) ?? 0) + 1);
    for (const s of selections) {
      if (s.kind !== 'plan' || !s.productionPlanId) continue;
      const plan = planById.get(s.productionPlanId);
      if (!plan) continue;
      pickedBy.set(plan.modelId, (pickedBy.get(plan.modelId) ?? 0) + (s.count ?? 0));
    }
    const out: string[] = [];
    for (const [modelId, count] of pickedBy) {
      const remaining = data?.remaining[modelId] ?? 0;
      if (count <= remaining) continue;
      const label = modelLabelOf(modelId);
      out.push(
        remaining > 0
          ? `${label} 모델은 ${remaining}대만 더 필요한데 ${count}대를 골랐습니다.`
          : `${label} 모델은 더 배정할 수 없는데 ${count}대를 골랐습니다.`,
      );
    }
    return out;
    // modelLabelOf 는 modelNameById/modelsLoading 파생이라 deps 에 원본을 넣는다.
  }, [selectedStock, selections, planById, data, modelNameById, modelsLoading]);

  // 같은 모델 안에서 사양(방향)별 수량을 넘기는 조합 — 하드 차단이 아니라
  // 확인 창(소프트 경고). 재고 사정상 의도적 스왑이 있을 수 있다.
  const specOverflowWarnings = useMemo(() => {
    const out: string[] = [];
    for (const line of data?.lines ?? []) {
      const dir = line.desiredDirection?.trim();
      if (!dir) continue;
      const n = selectedStock.filter(
        (r) =>
          r.modelId === line.modelId && (r.variantValue ?? '').toLowerCase() === dir.toLowerCase(),
      ).length;
      if (n > line.remainingCount) {
        out.push(
          `${modelLabelOf(line.modelId, line.modelCode)} / ${dir}: 남은 ${line.remainingCount}대보다 많은 ${n}대를 선택했습니다`,
        );
      }
    }
    return out;
  }, [data, selectedStock, modelNameById, modelsLoading]);

  const blocked = overflowWarnings.length > 0;
  const draftCount = selections.reduce((n, s) => n + (s.kind === 'stock' ? 1 : (s.count ?? 0)), 0);

  function onSave(): void {
    // round5 #2c(결정 2: 소프트 가드) — 사양 불일치 로봇 / 사양별 수량
    // 초과는 즉시 배정하지 않고 경고+확인. 하드 차단이 아니다.
    const mismatched = selectedStock
      .filter((r) => r.specMatch.tier === 'mismatch')
      .map(
        (r) =>
          `${r.serialShort ?? r.serialLong ?? r.id} (불일치: ${relabelVariantAxis(
            r.specMatch.mismatches,
            variantAxisLabelByModelId.get(r.modelId),
          ).join(', ')})`,
      );
    if (mismatched.length > 0 || specOverflowWarnings.length > 0) {
      setSoftWarnings({ mismatched, specOverflow: specOverflowWarnings });
      return;
    }
    onAssign();
  }

  // ── 왼쪽 목록 ────────────────────────────────────────────────────────

  const k = keyword.replace('#', '').trim().toLowerCase();
  const matchesKeyword = (r: StockRobot) =>
    !k || `${r.serialShort ?? ''} ${r.serialLong ?? ''}`.toLowerCase().includes(k);
  const freeStock = allStock.filter((r) => !draftRobotIds.includes(r.id) && matchesKeyword(r));
  // 개선계획 §2-2 — 기본은 완전일치만. 해제하면 맞음 · 일부만 맞음 · 다름 구역으로 나눠 다 보인다.
  // 2026-11-04 오너 "정렬 조건을 로봇 넘버순으로 하자" — 서버는 순서를 정하지 않아(DB 가 내주는 대로)
  // 번호가 섞여 보였다. 구역 안에서 로봇 번호순이다.
  const byNumber = (a: StockRobot, b: StockRobot) =>
    compareRobotNumber(a.serialShort, b.serialShort) ||
    robotName(a).localeCompare(robotName(b), 'ko', { numeric: true });
  const visibleStock = (
    exactOnly ? freeStock.filter((r) => passesExactFilter(r.specMatch.tier)) : [...freeStock]
  ).sort(byNumber);
  const hiddenStock = freeStock.length - visibleStock.length;
  const visiblePlans = exactOnly
    ? allPlans.filter((p) => passesExactFilter(p.specMatch.tier))
    : [...allPlans].sort((a, b) => b.specMatch.score - a.specMatch.score);
  const hiddenPlans = allPlans.length - visiblePlans.length;

  const robotRow = (r: StockRobot): PickRow => ({
    id: `r:${r.id}`,
    label: robotName(r),
    testId: 'assign-stock-row',
    title: '누르면 고르고, 두 번 누르면 맞는 빈자리에 바로 넣습니다',
    node: (
      <>
        <span className="asg-chip-top">
          <span className="asg-sn">{robotName(r)}</span>
          {/* 오너 결정 2026-10-21 — 허니컴로보틱스 로봇도 비욘드허니컴 계약에 배정된다.
              배정해도 소유 법인은 그대로라(회계 장부) 계약과 법인이 다른 로봇에만 법인
              이름을 붙인다. */}
          <RobotEntityTag
            robotEntity={r.ownerEntity}
            referenceEntity={data?.contract.ownerEntity}
          />
          <span className="asg-chip-badges">
            {r.isNew && <NewBadge />}
            {!fits(r.specMatch.tier) && (
              <SpecMatchBadge
                match={r.specMatch}
                variantAxisLabel={variantAxisLabelByModelId.get(r.modelId)}
              />
            )}
          </span>
        </span>
        <span className="asg-chip-meta">
          {formatRobotSpec(r, modelLabelOf(r.modelId)) ??
            `${modelLabelOf(r.modelId)} · 사양 미입력`}
        </span>
        <span className="asg-chip-meta">
          {[r.serialLong, robotStatusLabel(r.status)].filter(Boolean).join(' · ')}
        </span>
      </>
    ),
  });
  const tierGroups: Array<[string, (t: SpecMatchTier) => boolean]> = [
    ['희망 사양과 맞음', (t) => passesExactFilter(t)],
    ['일부만 맞음', (t) => t === 'partial'],
    ['희망 사양과 다름', (t) => t === 'mismatch'],
  ];
  const showAll = (
    <button type="button" className="asg-btn" data-size="sm" onClick={() => onExactOnly(false)}>
      모두 보기
    </button>
  );
  const sections: PickSection[] = [
    ...tierGroups.map(([title, test], gi) => {
      const rows = visibleStock.filter((r) => test(r.specMatch.tier)).map(robotRow);
      return {
        key: `stock-${gi}`,
        title:
          rows.length > 0 ? (
            <>
              {title}
              <span style={{ fontWeight: 500 }}>{rows.length}</span>
            </>
          ) : undefined,
        rows,
        after:
          gi === 0 ? (
            allStock.length === 0 ? (
              <div className="kuun-caption" data-testid="assign-stock-empty">
                이 모델의 재고 로봇이 없습니다. 아래 제조 계획에서 배정할 수 있습니다.
              </div>
            ) : visibleStock.length === 0 && hiddenStock > 0 ? (
              <div
                data-testid="assign-stock-exact-empty"
                style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
              >
                <span className="kuun-caption">
                  완전일치 로봇이 없습니다. 부분일치나 불일치인 {hiddenStock}대는 숨겨 두었습니다.
                </span>
                {showAll}
              </div>
            ) : exactOnly && hiddenStock > 0 ? (
              <div className="kuun-caption">
                부분일치나 불일치인 {hiddenStock}대는 숨겨 두었습니다.
              </div>
            ) : undefined
          ) : undefined,
      };
    }),
    {
      key: 'plans',
      // 제조 계획 — 재고가 없을 때의 핵심 경로라 항상 남긴다.
      title: '앞으로 나올 로봇(제조 계획)',
      rows: visiblePlans.map((p) => ({
        id: `p:${p.id}`,
        label: p.humanId,
        tone: p.isFeasible ? ('plan' as const) : ('late' as const),
        disabled: !p.isFeasible || p.availableCount - planCount(p.id) <= 0,
        title: p.isFeasible
          ? '설치일 전에 완성할 수 있습니다. 끌어다 놓을 때마다 한 대씩 들어갑니다.'
          : '완성 예정일이 설치일과 여유 기간보다 늦습니다. 관리자만 배정할 수 있습니다.',
        testId: 'assign-plan-row',
        node: (
          <>
            <span className="asg-chip-top">
              <span className="asg-sn" data-plain>
                {p.humanId}
              </span>
              <span className="asg-chip-badges">
                {!fits(p.specMatch.tier) && (
                  <SpecMatchBadge
                    match={p.specMatch}
                    variantAxisLabel={variantAxisLabelByModelId.get(p.modelId)}
                  />
                )}
              </span>
            </span>
            {/* S2 Phase 2.9 — "GEN2 · White · Right" spec 표현 */}
            <span className="asg-chip-meta">{formatPlanSpec(p, modelLabelOf(p.modelId))}</span>
            {/* 추가수정 2차(26-07-27) 정정2 — 완성 예정일 표기는 kstYmd(다른 화면과 같은 "2026.08.26"). */}
            <span className="asg-chip-meta">
              완성 예정 {kstYmd(p.estimatedCompletionAt) ?? '–'} · 가용{' '}
              {p.availableCount - planCount(p.id)} / {p.plannedCount}
              {!p.isFeasible && ' · 설치일보다 늦음'}
            </span>
          </>
        ),
      })),
      after:
        allPlans.length === 0 ? (
          <div className="kuun-caption">관련 제조 계획이 없습니다.</div>
        ) : visiblePlans.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="kuun-caption">
              완전일치 제조 계획이 없습니다. 부분일치나 불일치인 {hiddenPlans}건은 숨겨 두었습니다.
            </span>
            {showAll}
          </div>
        ) : undefined,
    },
  ];
  const listCount = visibleStock.length;

  const required = requiredOf(c);
  const assigned = assignedOf(c);
  const salesOwner = c.customer.salesOwnerCode?.trim() || '—';
  const d = dayInfo(c.scheduledInstallAt);

  return (
    <div className="asg-board" data-testid="assign-board">
      <PickPanel
        count={listCount}
        pickedCount={picked.size}
        onPutPicked={putPicked}
        keyword={keyword}
        onKeyword={setKeyword}
        exactOnly={exactOnly}
        onExactOnly={onExactOnly}
        showTools={canPlace}
      >
        {gated ? (
          // 추가수정(26-07-27) §1-4 참고 — 선택 뒤 고객사 상태가 배정 불가로 바뀐 경우.
          // 후보 질의가 막혀 있어 이 분기가 없으면 '후보 로딩 실패'라는 거짓 오류가 뜬다.
          <div className="asg-pick-note">
            {customerStatusLabel(c.customerStatus)} 상태라 배정할 수 없습니다.
          </div>
        ) : full ? (
          // 추가수정(26-07-27) §1-4 — 필요 수량이 찬 계약은 후보 대신 교체 경로 안내.
          // 배정 0대 · 부분 배정인 설치 대기 행은 여기서 빠진다(selectedStuck 주석).
          <div className="asg-pick-note">
            필요 수량을 모두 채웠습니다. 로봇을 바꾸려면 무대에서 그 로봇의 '교체'를 눌러 주세요.
          </div>
        ) : candidatesLoading ? (
          <div className="asg-pick-note">불러오는 중…</div>
        ) : candidatesError || !data ? (
          <div className="asg-pick-note" style={{ color: 'var(--fg-danger)' }}>
            후보를 불러오지 못했습니다.
          </div>
        ) : (
          <AssignPickList
            sections={sections}
            picked={picked}
            onPickedChange={setPicked}
            onPut={(ids) => put(ids, chipFrom())}
            loadOf={(ids) => ({
              robotIds: ids.filter((x) => x.startsWith('r:')).map((x) => x.slice(2)),
              planIds: ids.filter((x) => x.startsWith('p:')).map((x) => x.slice(2)),
            })}
            onDragLoad={setDragLoad}
            canDrag={canPlace}
            testId="assign-stock-list"
          />
        )}
      </PickPanel>

      <AssignStage
        ref={stageRef}
        testId="assign-stage"
        canDrop={canPlace}
        dragging={!!dragLoad}
        flyRequest={fly}
        lines={stageLines}
        empty={<div className="asg-floor-empty">계약 줄 정보가 없습니다.</div>}
        onDrop={(load, slot, point) => {
          setDragLoad(null);
          // 배정된 로봇 위에 놓으면 바꾸기 — 그 로봇을 풀고 놓은 로봇을 넣어 둔다(확인 창을 거친다).
          if (slot?.replaceable && load.robotIds.length > 0) {
            const old = robots.find((r) => slot.key === `saved:${r.id}`);
            const incoming = stockById.get(load.robotIds[0]);
            if (old && incoming) {
              onReplace(old, { id: incoming.id, label: robotName(incoming) });
              return;
            }
          }
          put(
            [...load.robotIds.map((id) => `r:${id}`), ...load.planIds.map((id) => `p:${id}`)],
            () => point,
          );
        }}
        header={
          <header className="asg-stage-head">
            <div className="asg-stage-title">
              {/* 2026-11-04 오너 "눌렀을때 고객사 마스터 페이지 해당 매장 페이지로 넘어가게 해줘" —
                  고객사 마스터 목록의 상호 링크와 같은 곳(고객사 상세). */}
              <h2>
                <Link
                  to={`/customers/${c.customer.id}`}
                  className="asg-stage-link"
                  title="고객사 상세로 이동"
                  data-testid="assign-customer-link"
                >
                  {c.customer.displayName}
                </Link>
              </h2>
              <StageBadge assigned={assigned} required={required} />
              {/* 같은 고객사 계약이 2건 이상일 때 구분이 필요해 작은 글씨로 남긴다. */}
              <span className="asg-humanid" data-testid="assign-contract-humanid">
                {c.humanId}
              </span>
            </div>
            <div className="asg-kv">
              <span>
                설치 예정<b>{fullDate(c.scheduledInstallAt)}</b>
              </span>
              <span>
                남은 날
                <b data-soon={isSoon(d.days, required > 0 && assigned >= required) || undefined}>
                  {d.left}
                </b>
              </span>
              <span>
                담당 영업<b>{salesOwner}</b>
              </span>
              {data && (
                <span>
                  여유 기간<b>{data.bufferDays}일</b>
                </span>
              )}
            </div>
          </header>
        }
        notes={
          <>
            {replacementPending && (
              <div className="asg-stage-note" data-tone="warn">
                배정을 해제했습니다. 바꿀 로봇을 넣고 저장해 주세요.
              </div>
            )}
            {siblingAwaitingCount > 0 && (
              <div className="asg-stage-note" data-tone="warn">
                이 고객사에는 배정 대기 계약이 {siblingAwaitingCount + 1}건 있어서 이미 나가 있는
                로봇이 자동으로 연결되지 않습니다. 이 계약({c.humanId})에 직접 배정해 주세요.
              </div>
            )}
            {modelsError && (
              <div className="asg-stage-note" style={{ color: 'var(--fg-danger)' }}>
                모델 정보를 불러오지 못했습니다. 이름 대신 ID 앞부분이 보일 수 있습니다.{' '}
                <button type="button" className="asg-btn" data-size="sm" onClick={onRetryModels}>
                  재시도
                </button>
              </div>
            )}
            {overflowWarnings.length > 0 && (
              <div
                className="asg-stage-note"
                data-tone="warn"
                data-testid="assign-overflow-warning"
                role="alert"
              >
                {overflowWarnings.map((w) => (
                  <div key={w}>{w}</div>
                ))}
              </div>
            )}
          </>
        }
        hint={
          canPlace
            ? '왼쪽 로봇을 여기로 끌어다 놓으면 맞는 빈자리에 들어갑니다. 배정된 로봇 위에 놓으면 바꿉니다.'
            : undefined
        }
        footer={
          <div className="asg-save">
            <span className="asg-save-text">
              {draftCount > 0
                ? `저장 전 ${draftCount}대. 저장해야 배정됩니다.`
                : picked.size > 0
                  ? `고른 로봇 ${picked.size}대. 무대로 끌거나 [${picked.size}대 넣기]를 누르세요.`
                  : `배정 ${assigned}/${required}대`}
            </span>
            {draftCount > 0 && (
              <button
                type="button"
                className="asg-btn"
                data-variant="ghost"
                onClick={() => onChange([])}
              >
                되돌리기
              </button>
            )}
            <button
              type="button"
              className="asg-btn"
              onClick={fill}
              disabled={!canPlace || [...emptyBy.values()].every((n) => n === 0)}
            >
              맞는 로봇으로 채우기
            </button>
            <button
              type="button"
              className="asg-btn"
              data-variant="primary"
              data-testid="assign-save"
              disabled={draftCount === 0 || isAssigning || blocked}
              onClick={onSave}
            >
              {isAssigning ? '배정 중…' : `배정 저장 (${draftCount}대)`}
            </button>
          </div>
        }
      />

      <ConfirmDialog
        open={softWarnings !== null}
        title={
          softWarnings && softWarnings.mismatched.length > 0
            ? '희망 사양과 다른 로봇 배정'
            : '사양별 수량 초과'
        }
        message={[
          softWarnings && softWarnings.mismatched.length > 0
            ? `선택한 로봇 중 ${softWarnings.mismatched.length}대가 영업 희망 사양과 일치하지 않습니다.\n\n${softWarnings.mismatched.join('\n')}`
            : null,
          softWarnings && softWarnings.specOverflow.length > 0
            ? `사양별 필요 수량을 넘습니다.\n\n${softWarnings.specOverflow.join('\n')}`
            : null,
          '그래도 배정하시겠습니까?',
        ]
          .filter((s): s is string => !!s)
          .join('\n\n')}
        confirmLabel="그대로 배정"
        danger
        busy={isAssigning}
        onConfirm={() => {
          setSoftWarnings(null);
          onAssign();
        }}
        onCancel={() => setSoftWarnings(null)}
      />
    </div>
  );
}

/** 제조 계획 한 건의 대수를 바꾼 선택 목록 — 0 이하면 뺀다. */
function withPlanCount(sels: AssignSelection[], planId: string, count: number): AssignSelection[] {
  const others = sels.filter((s) => !(s.kind === 'plan' && s.productionPlanId === planId));
  return count > 0 ? [...others, { kind: 'plan', productionPlanId: planId, count }] : others;
}

const flagStyle: CSSProperties = {
  flexShrink: 0,
  fontSize: 10,
  fontWeight: 700,
  padding: '1px 5px',
  borderRadius: 'var(--r-pill)',
  whiteSpace: 'nowrap',
};
